import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { formatCount } from '../../../../utils/sms';

export interface CustomizableTab {
  /** Label shown in the tab */
  title: string;
  /** SVG path `d` attribute for an optional 24×24 icon */
  icon?: string;
  /** Optional count shown next to the title */
  count?: number;
  /** Router path — when set the component operates in routing mode */
  link?: string;
  /** Query params forwarded to [queryParams] (routing mode only) */
  queryParams?: Record<string, string>;
}

let nextTabsUid = 0;

/**
 * Segmented tabs in the console style (same look as app-routing-tab):
 * a sunken pill track with a thumb that glides to the active tab.
 *
 * Routing mode  — provide `link` on every tab; the active tab is derived
 *                 from the current URL and navigation events are tracked.
 *
 * View mode     — omit `link`; the parent controls the active tab via the
 *                 `activeTab` input and listens to `(tabChange)` output.
 *                 The component also manages its own internal state so it
 *                 works without any external bindings.
 *
 * Usage (routing):
 *   <customizable-tabs [tabs]="[{ title: 'Foo', link: '/foo' }, ...]" />
 *
 * Usage (view / click):
 *   <customizable-tabs [tabs]="tabs" [activeTab]="current" (tabChange)="current = $event" />
 *
 * In view mode pass `panelId` (the id of the element the tabs switch) and
 * label that panel with the active tab:
 *   <customizable-tabs #views [tabs]="tabs" panelId="review-panel" ... />
 *   <div id="review-panel" role="tabpanel" [attr.aria-labelledby]="views.activeTabId">…</div>
 */
@Component({
  selector: 'customizable-tabs',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './customizable-tabs.component.html',
  styleUrl: './customizable-tabs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomizableTabsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tabs: CustomizableTab[] = [];

  /**
   * When true the track fills its container and each tab grows equally.
   * Use this for full-width tab bars (e.g. 4 equal tabs across a panel).
   */
  @Input() stretch = false;

  /** Controlled active index for view mode. Ignored in routing mode. */
  @Input() set activeTab(idx: number) {
    if (!this.isRoutingMode) {
      this._activeIndex = idx;
      this.schedulePlaceThumb();
      this.cdr.markForCheck();
    }
  }

  /** View mode: id of the panel the tabs control (sets aria-controls). */
  @Input() panelId: string | null = null;

  /** Emitted with the new tab index on every tab click (both modes). */
  @Output() tabChange = new EventEmitter<number>();

  @ViewChildren('tabEl') tabEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('outer') outerEl?: ElementRef<HTMLElement>;
  @ViewChild('thumb') thumbEl?: ElementRef<HTMLElement>;

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Counts read as plain figures with thousands separators: 1,204. */
  readonly formatCount = formatCount;

  _activeIndex = 0;

  private subs = new Subscription();
  private resizeObserver?: ResizeObserver;
  /** The tab the thumb last settled on; -1 until it is first placed. */
  private thumbIndex = -1;

  private readonly uid = nextTabsUid++;

  /** Stable id of the tab button at `index` (view mode). */
  tabId(index: number): string {
    return `bk-tab-${this.uid}-${index}`;
  }

  /** Id of the active tab, for the panel's aria-labelledby. */
  get activeTabId(): string {
    return this.tabId(this._activeIndex);
  }

  get isRoutingMode(): boolean {
    return this.tabs.some((t) => t.link !== undefined);
  }

  ngOnInit(): void {
    if (this.isRoutingMode) {
      this.syncActiveIndex(this.router.url);
      this.subs.add(
        this.router.events
          .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
          .subscribe((e) => {
            this.syncActiveIndex(e.urlAfterRedirects);
            this.schedulePlaceThumb();
          }),
      );
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    // Re-measure whenever a tab changes size: the font arriving, counts
    // loading, or the strip first becoming visible inside a modal.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.placeThumb());
      this.observeTabs();
    }
    this.schedulePlaceThumb();

    this.subs.add(
      this.tabEls.changes.subscribe(() => {
        this.observeTabs();
        this.schedulePlaceThumb();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  selectTab(index: number): void {
    this._activeIndex = index;
    this.schedulePlaceThumb();
    this.tabChange.emit(index);
    this.cdr.markForCheck();
  }

  /** Arrow keys move between tabs in view mode, as a tablist should. */
  onKeydown(event: Event, index: number): void {
    const key = (event as KeyboardEvent).key;
    const last = this.tabs.length - 1;
    let next = -1;
    if (key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (key === 'Home') next = 0;
    else if (key === 'End') next = last;
    if (next < 0) return;
    event.preventDefault();
    this.selectTab(next);
    this.tabEls.get(next)?.nativeElement.focus();
  }

  private syncActiveIndex(url: string): void {
    const [path, search = ''] = url.split('?');
    const urlParams = new URLSearchParams(search);
    let bestIdx = -1;
    let bestLen = 0;

    this.tabs.forEach((tab, i) => {
      if (!tab.link || !path.startsWith(tab.link) || tab.link.length < bestLen) return;

      if (tab.queryParams !== undefined) {
        const keys = Object.keys(tab.queryParams);
        if (keys.length === 0) {
          const siblingMatches = this.tabs.some(
            (other) =>
              other.queryParams !== undefined &&
              Object.keys(other.queryParams).length > 0 &&
              Object.entries(other.queryParams).every(
                ([k, v]) => urlParams.get(k) === String(v)
              )
          );
          if (siblingMatches) return;
        } else {
          if (!keys.every((k) => urlParams.get(k) === String(tab.queryParams![k]))) return;
        }
      }

      bestIdx = i;
      bestLen = tab.link!.length;
    });

    this._activeIndex = bestIdx >= 0 ? bestIdx : 0;
    this.tabChange.emit(this._activeIndex);
    this.cdr.markForCheck();
  }

  private observeTabs(): void {
    const observer = this.resizeObserver;
    if (!observer) return;
    observer.disconnect();
    this.tabEls.forEach((tab) => observer.observe(tab.nativeElement));
  }

  private schedulePlaceThumb(): void {
    if (!this.isBrowser) return;
    setTimeout(() => this.placeThumb());
  }

  /**
   * Sizes the thumb to the active tab and slides it there with a transform.
   * Writes straight to the element so it never waits on change detection.
   */
  private placeThumb(): void {
    const thumb = this.thumbEl?.nativeElement;
    const tab = this.tabEls?.get(this._activeIndex)?.nativeElement;
    if (!thumb || !tab || !tab.offsetWidth) return;

    thumb.style.width = `${tab.offsetWidth}px`;
    thumb.style.transform = `translateX(${tab.offsetLeft}px)`;

    if (this.thumbIndex === this._activeIndex) return;
    const firstPlacement = this.thumbIndex === -1;
    this.thumbIndex = this._activeIndex;
    this.revealTab(tab, !firstPlacement);

    if (firstPlacement) {
      // Commit the first position before transitions switch on, so the
      // thumb appears in place rather than sliding in from the left edge.
      thumb.classList.add('is-placed');
      thumb.getBoundingClientRect();
      thumb.classList.add('is-live');
    }
  }

  /** Scrolls an overflowing strip just far enough to show the active tab. */
  private revealTab(tab: HTMLElement, smooth: boolean): void {
    const outer = this.outerEl?.nativeElement;
    if (!outer || outer.scrollWidth <= outer.clientWidth) return;

    const box = outer.getBoundingClientRect();
    const rect = tab.getBoundingClientRect();
    const gutter = 24;
    let delta = 0;
    if (rect.left < box.left) delta = rect.left - box.left - gutter;
    else if (rect.right > box.right) delta = rect.right - box.right + gutter;
    if (!delta) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    outer.scrollBy({ left: delta, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  }
}
